import { v4 as uuid } from 'uuid';
import pkceChallenge from 'pkce-challenge';
import ResponseHelper from './response-helper';
import DeviceStorage from '../device-storage';

/**
 * Lib that manages communication with Vizzto server related to authorization and user session.
 * Returns with `code = 0` are considered sucessfull.
 */

/**
 * @typedef FieldErrorResponse All methods can return a failed promise with field validation errors following this structure.
 * @type {Object}
 * @property {Number} code Field validation error code (400)
 * @property {String} message Global error message (like: "Some fields are invalid.").
 * @property {Object} data Holds the errors. Each property is related to a field error. Property's key must be the field name. Property's value must be the error message.
 * @property {Object} metadata this property metadata is third party code or optional data
 * @property {String} arenaId this property arenaId is returned for Arena Chat API
 * @example { code: 400, message: "Request is invalid.", data: { user: "User cannot have more than 3 chars.", password: "Password doesn't have special char." } }
 */

const loginResponseAdapter = ({ data }) => {
  return Promise.resolve({
    data: {
      id: data.user.id,
      name: data.user.name,
      username: data.user.username,
      socialName: data.user.socialName,
      linkedin: data.user.linkedin,
      profilePicture: data.user.profilePicture,
      city: data.user.city,
      state: data.user.state,
      country: data.user.country,
      profileCompleted: data.user.profileCompleted,
      age: data.user.age,
      token: data.session.token,
      refreshToken: data.session.refreshToken,
      deviceManagementId: data.user.deviceManagementId, // This is used in PLAY and CONTENT BUYING.
      incompleteProfile: !!data?.signup?.showSignup,
      metadata: {
        arenaId: data.user.arenaId
      },
      signupTypes: data?.signup?.signupTypes || []
    },
    code: 0, // Code = 0 means user logged in sucessfully
    message: 'OK'
  });
};

export default (axios) => ({
  /**
   * Checks whether user is logged in.
   * - must pass the utoken through the request header.
   * @see {@link https://vspuat30-gateway.vizzto.net/swagger-ui.html#/session-resource}
   * @param {String} utoken User token
   * @returns {Promise<{data: Object, code: Number, message: String}>} Resolved promise with logged in status or rejected promise on error
   */
  async isLoggedIn(utoken) {
    return axios({
      method: 'get',
      url: 'sessions',
      headers: {
        utoken
      }
    })
      .then(loginResponseAdapter)
      .catch((error) => Promise.reject(ResponseHelper.getErrorResponse(error)));
  },

  async govLogin({ code, distributorId, device, language, country }) {
    const codeVerifier = DeviceStorage.get('verifier');

    return axios({
      method: 'post',
      url: 'auth/gov',
      data: {
        code,
        codeVerifier,
        distributorId,
        language,
        country,
        deviceType: device.type,
        deviceId: device.id,
        deviceName: device.name,
        devicePlatform: device.platform,
        deviceUserAgent: device.userAgent,
        systemVersion: device.systemVersion
      }
    }).then(loginResponseAdapter);
  },

  async redirectToGovLogin(clientId, redirectUri, loginUrl) {
    const pkce = await pkceChallenge();

    DeviceStorage.set('verifier', pkce.code_verifier);

    const ssoParams = {
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: 'openid email profile govbr_confiabilidades',
      nonce: uuid(),
      state: uuid(),
      code_challenge: pkce.code_challenge,
      code_challenge_method: 'S256'
    };

    const url = new URL(`${loginUrl}/authorize`);

    Object.keys(ssoParams).forEach((paramName) => {
      url.searchParams.append(paramName, ssoParams[paramName]);
    });

    window.open(url, '_self', 'noopener noreferrer');
  },

  /**
   * Performs login.
   * @see {@link https://vspuat30-gateway.vizzto.net/swagger-ui.html#/session-resource}
   * @param {number} distributorId Distributor id
   * @param {String} uname User username
   * @param {String} password User password
   * @param {Object} device Browser fringerprint. This is only necessary on login.
   * @param {String} device.type
   * @param {String} device.id
   * @param {String} device.name
   * @param {String} device.platform
   * @param {String} device.userAgent
   * @param {String} device.systemVersion
   * @param {String} language PT-BR
   * @param {String} country BR
   * @param {boolean} isAnonymous Whether to perform anonymous login
   * @returns {Promise<{data: Object, code: Number, message: String}>} Resolved promise with login result or rejected promise on error
   */
  async login(
    distributorId,
    uname,
    password,
    isAnonymous,
    device,
    language,
    country
  ) {
    return axios({
      url: 'sessions/login',
      method: 'post',
      data: {
        distributorId,
        uname,
        password,
        anonymous: isAnonymous,
        deviceType: device.type,
        deviceId: device.id,
        deviceName: device.name,
        devicePlatform: device.platform,
        deviceUserAgent: device.userAgent,
        systemVersion: device.systemVersion,
        language,
        country
      }
    })
      .then(loginResponseAdapter)
      .catch((error) => Promise.reject(ResponseHelper.getErrorResponse(error)));
  },

  /**
   * Performs logout.
   * @see {@link https://vspuat30-gateway.vizzto.net/swagger-ui.html#/session-resource}
   * @param {String} utoken User token
   * @returns {Promise} Resolved promise on sucessful logout or rejected promise on error
   */
  logout(utoken) {
    return axios({
      url: 'sessions',
      method: 'delete',
      headers: {
        utoken // Must pass the utoken through the request header.
      }
    })
      .then(function () {
        return Promise.resolve({
          data: {}, // return code 204 to confirm the request
          code: 0, // Code = 0 means user logged out sucessfully
          message: 'OK'
        });
      })
      .catch(function (error) {
        return Promise.reject(ResponseHelper.getErrorResponse(error));
      });
  },

  /**
   * Request information about "anonymous login" (whether user can enter as anonymous).
   * @param {String} distributorId
   * @returns {Promise<{data: {hasAnonymous: (boolean|*), name, id}}>}
   */
  getDistributorAnonymous(distributorId) {
    return axios
      .$get(`distributors/${distributorId}/list/released`)
      .then(function (response) {
        if (response?.length) {
          const d = response[0];
          return Promise.resolve({
            data: {
              id: d.id,
              name: d.name,
              hasAnonymous: d.anonymous,
              gateway: d.gateway,
              gatewayPublicKey: d.gatewayPublicKey,
              useGovLogin: !!d?.useGovLogin,
              govClientId: d?.govClientId,
              govRedirectUri: d?.govRedirectUri,
              hasTermsOfUse: d.hasTermsOfUse,
              hasPrivacyPolicy: d.hasPrivacyPolicy
            }
          });
        }
        return Promise.reject(response);
      })
      .catch(function (error) {
        return Promise.reject(ResponseHelper.getErrorResponse(error));
      });
  },

  /**
   * @param {String} uname
   * @param {String} utoken
   */
  async getUserUname(uname, utoken) {
    return axios({
      method: 'get',
      url: `users/uname/${uname}`,
      headers: {
        utoken
      }
    })
      .then(function (response) {
        return Promise.resolve(response.data.description[0]);
      })
      .catch(function (error) {
        return Promise.reject(ResponseHelper.getErrorResponse(error));
      });
  },
  async refresh(refreshToken) {
    return axios({
      method: 'post',
      url: 'sessions/refresh',
      data: {
        refreshToken
      },
      skipAuth: true,
      headers: {
        utoken: false
      }
    })
      .then(loginResponseAdapter)
      .catch((error) => Promise.reject(ResponseHelper.getErrorResponse(error)));
  }
});
